// 2026-08-24 - FormuLearning - Step 2 pH별 LogD·용해도 프로파일 (M84-45, 이슈 #62)
// 파일 위치: frontend/src/features/step2/PhProfileSection.tsx
//
// 요건 정본: 변경등록부 `M84-45`(안건8) — "pH별 LogD와 Solubility 를 PharmAI 와 같이 그래프로".
// 구 PharmAI 는 계열마다 **1행 표 + line 차트**를 나란히 놓았다
// (old/260413/.../pharmai/formulation/selectInput.jsp:367·:422). 그 배치를 따른다.
//
// 값은 백엔드가 유도해 `ai_prediction._ph_profile` 로 실어 보낸다(step2/ph_profile.py).
// 화면은 계산하지 않는다 — 표와 두 그래프가 같은 배열을 본다.
//
// 2026-09-13 (M901-12·13, 이슈 #74): 한 표 2행 + 그래프 2개 나란히 → **계열별 블록**
// (LogD 표 → LogD 그래프 / Solubility 표 → Solubility 그래프). 260901 회의 *"표를 둘로 쪼개고
// 각 표 아래에 각 그래프"*. 격자는 15점(pH 1.2·4.5·6.8 추가) — 화면은 받은 배열을 그대로 그린다.
// 정본 = 요구사항정의서 v2.10 REQ-FN-2-04 a·b·h.
//
// 2026-09-23 (M915-06·07, 이슈 #80): 격자 17점(pH 11·12 추가). 용해도 추정치 1,000 g/L 이상은
// 백엔드가 null 로 가려 보낸다 — 표는 '—', 그래프는 끊긴 선, 가린 점이 있으면 각주로 알린다.
//
// 2026-10-29 (이슈 #111): M1016-10 상한 이상은 백엔드가 1,000 으로 잘라 보낸다 — 표 «≥1000», 그래프는 1,000 에서
// 평탄(상한 점은 속 빈 표식). M1016-11 `profile_reliable = N` 이면 경고 배지 + 사유 툴팁. M1016-12 «모체(유리형)
// 기준 · 예측값» 표기와 상한 구간 각주. 각주 문언은 LogD 식 교체 여부(Q-261029-03) 회신에 따라 다시 본다.

import { useEffect, useRef, type ReactNode } from 'react'
import type { PhProfile, ProfileQuality } from '../../api/step2'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'

interface Props {
  profile?: PhProfile
  quality?: ProfileQuality
}

/** 상한(1,000 g/L) 이상 = «매우 잘 녹음» 범주. 값이 없는 구간이 아니다(국민대 README). */
function fmtSolubilityCell(v: number | null | undefined, max: number): string {
  if (v != null && Number.isFinite(v) && v >= max) return `≥${max.toLocaleString()}`
  return fmtSolubility(v)
}

/** profile_reliable = N 경고 — 사유는 툴팁(국민대 UI 요청 1). */
function ReliabilityBadge({ quality }: { quality?: ProfileQuality }) {
  if (!quality || quality.reliable) return null
  const reasons = quality.reasons.length > 0 ? quality.reasons : ['예측 입력의 신뢰도가 낮습니다.']
  return (
    <span
      className="ml-2 inline-flex cursor-help items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-warn) 45%, transparent)',
        background: 'color-mix(in srgb, var(--color-warn) 12%, transparent)',
        color: 'var(--color-warn)',
      }}
      title={reasons.map((r) => `• ${r}`).join('\n')}
    >
      ⚠ 참고용 — 신뢰도 낮음
    </span>
  )
}

/** 용해도는 자릿수가 크게 벌어진다(0.05 ~ 15,810 g/L). 유효숫자로 다듬는다. */
function fmtSolubility(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  if (v !== 0 && (Math.abs(v) < 1e-2 || Math.abs(v) >= 1e5)) return v.toExponential(2)
  return String(Number(v.toPrecision(4)))
}

function fmtLogd(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : v.toFixed(2)
}

function fmtPh(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

/** 1행 표 — pH 머리행 + 계열 값 행. 계열마다 한 벌씩 쓴다.
 *  셀 여백 px-2: 15점 격자의 용해도(유효숫자 4자리) 표가 1600px 화면 카드 폭에 들어오게 한다(px-3 은 94px 넘침).
 *  17점(#80)에서 넘치면 가로 스크롤로 읽는다. */
function SeriesTable({ ph, label, cells }: { ph: number[]; label: string; cells: string[] }) {
  return (
    <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
      <table className="w-full text-[11px]">
        <thead>
          <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
            <th className="px-2 py-1.5 text-left font-medium text-text-secondary whitespace-nowrap">pH</th>
            {ph.map((p) => (
              <th key={p} className="px-2 py-1.5 text-center font-medium text-text-secondary whitespace-nowrap">{fmtPh(p)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
            <td className="px-2 py-1.5 font-medium text-text-body whitespace-nowrap">{label}</td>
            {cells.map((c, i) => (
              <td key={ph[i]} className="px-2 py-1.5 text-center text-text-body whitespace-nowrap">{c}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/** 계열 블록 — 표 아래에 그 계열의 그래프(REQ-FN-2-04 a). */
function SeriesBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-medium text-text-body">{title}</p>
      {children}
    </div>
  )
}

/** 그래프 틀 — 제목 한 줄 + Plotly 가 그릴 자리. */
function ChartFrame({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <div className="mt-2 rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
      <p className="mb-1 px-1 text-[10px] text-text-secondary">{caption}</p>
      {children}
    </div>
  )
}

export default function PhProfileSection({ profile, quality }: Props) {
  const logdRef = useRef<HTMLDivElement>(null)
  const solRef = useRef<HTMLDivElement>(null)
  const theme = useUiStore((s) => s.theme)

  const ph = profile?.ph ?? []
  const logd = profile?.logd ?? []
  const solubility = profile?.solubility ?? null
  const hasProfile = ph.length > 0
  const solMax = profile?.solubility_max ?? 1000
  // 상한 이상은 1,000 으로 잘려 온다(M1016-10). null 은 상한 도입 전 규칙의 잔재가 있을 때만 생긴다.
  const solCapped = solubility ? solubility.filter((v) => v != null && v >= solMax).length : 0
  const solShown = solubility ? solubility.filter((v) => v != null).length : 0

  // 의존성은 `profile` 하나만 둔다 — `?? []` 폴백은 렌더마다 새 배열이라, 그것을 의존성에
  // 넣으면 차트가 매 렌더 다시 그려진다.
  useEffect(() => {
    if (!profile || profile.ph.length === 0) return
    const { ph, logd, solubility } = profile
    const max = profile.solubility_max ?? 1000
    let cancelled = false
    const t = chartTokens()

    const base = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: t.plotBg,
      font: { color: t.font, size: 10 },
      margin: { t: 20, r: 20, b: 40, l: 55 },
      showlegend: false,
    }

    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled) return

      if (logdRef.current) {
        Plotly.newPlot(logdRef.current, [{
          x: ph,
          y: logd,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'LogD',
          line: { color: t.purple, width: 2 },
          marker: { size: 5, color: t.purple },
          hovertemplate: 'pH %{x}<br>LogD %{y:.2f}<extra></extra>',
        }], {
          ...base,
          xaxis: { title: { text: 'pH' }, gridcolor: t.grid, dtick: 1 },
          yaxis: { title: { text: 'LogD' }, gridcolor: t.grid, zeroline: true, zerolinecolor: t.grid },
        }, { displayModeBar: false, responsive: true })
      }

      // 용해도는 자릿수가 벌어지므로 로그 축이 아니면 저 pH 구간이 바닥에 눌려 읽히지 않는다.
      if (solRef.current && solubility && solubility.some((v) => v != null)) {
        Plotly.newPlot(solRef.current, [{
          x: ph,
          y: solubility,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Solubility',
          line: { color: t.teal, width: 2 },
          // 상한 점은 속 빈 표식 — 1,000 은 «≥1000» 범주라 실제 값과 구분한다(M1016-10)
          marker: {
            size: 5, color: t.teal,
            symbol: solubility.map((v) => (v != null && v >= max ? 'circle-open' : 'circle')),
          },
          customdata: solubility.map((v) => (v != null && v >= max ? `≥${max.toLocaleString()}` : null)),
          hovertemplate: solubility.map((v) => (v != null && v >= max
            ? 'pH %{x}<br>%{customdata} g/L<extra></extra>'
            : 'pH %{x}<br>%{y:.4g} g/L<extra></extra>')),
        }], {
          ...base,
          xaxis: { title: { text: 'pH' }, gridcolor: t.grid, dtick: 1 },
          yaxis: { title: { text: 'Solubility (g/L)' }, type: 'log', gridcolor: t.grid },
        }, { displayModeBar: false, responsive: true })
      }
    })

    return () => { cancelled = true }
  }, [profile, theme])

  if (!hasProfile) {
    return (
      <div className="mb-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">pH Profile (LogD · Solubility)</h4>
        <p className="text-[11px] text-text-secondary">
          Run DB Search &amp; AI Prediction to derive the pH profile. (LogP·pKa 예측이 있어야 산출됩니다.)
        </p>
      </div>
    )
  }

  return (
    <div className="mb-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
        pH Profile (LogD · Solubility)
        <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-text-muted">모체(유리형) 기준 · 예측값</span>
        <ReliabilityBadge quality={quality} />
      </h4>

      <div className="space-y-4">
        <SeriesBlock title="LogD">
          <SeriesTable ph={ph} label="LogD" cells={logd.map(fmtLogd)} />
          <ChartFrame caption="pH — LogD">
            <div ref={logdRef} style={{ width: '100%', height: 220 }} />
          </ChartFrame>
        </SeriesBlock>

        <SeriesBlock title="Solubility (g/L)">
          {solubility ? (
            <>
              <SeriesTable ph={ph} label="Solubility (g/L)" cells={solubility.map((v) => fmtSolubilityCell(v, solMax))} />
              {solShown > 0 ? (
                <ChartFrame caption="pH — Solubility (g/L, log scale)">
                  <div ref={solRef} style={{ width: '100%', height: 220 }} />
                </ChartFrame>
              ) : null}
              {solCapped > 0 && (
                <p className="mt-1 text-[10px] text-text-muted">
                  ≥{solMax.toLocaleString()} = 매우 잘 녹음(Very soluble). 이 이상은 활동도 비이상성으로 정량적 의미가 없어
                  {' '}{solMax.toLocaleString()} g/L 에서 평평하게 그립니다({solCapped}점, 그래프의 속 빈 점).
                  이 구간에서는 용해도와 LogD 의 관계식이 적용되지 않습니다.
                </p>
              )}
            </>
          ) : (
            // 용해도 산출 불가 시 블록 자리에 안내문만 둔다 — '—' 로 채운 표는 값이 비었다는 사실을 흐린다(REQ-FN-2-04 h).
            <div
              className="flex items-center justify-center rounded border text-center text-[10px] text-text-muted"
              style={{ height: 80, borderColor: 'var(--border-subtle)' }}
            >
              데이터 없음 — AI 예측에 수용해도 또는 LogP 가 없어<br />pH별 용해도를 산출할 수 없습니다.
            </div>
          )}
        </SeriesBlock>
      </div>

      <p className="mt-1.5 text-[10px] text-text-muted">
        LogD·용해도 모두 LogP·pKa 에서 유도한 <strong>이론(Henderson–Hasselbalch) 프로파일</strong>이며 실측값이 아닙니다.
        염으로 입력해도 <strong>모체(유리형) 기준</strong>입니다 — 염 형태의 절대 용해도는 구조만으로 예측할 수 없습니다.
        용해도는 수용해도({profile?.s0 != null ? `${fmtSolubility(profile.s0)} g/L` : '—'})를 기준으로 이온화 분율을 곱한 값이라,
        염 형성으로 생기는 상한(pH<sub>max</sub>)은 반영하지 않습니다.
      </p>
    </div>
  )
}
